import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Tie } from '../../_shared/models/tie.model';
import { AuthService } from '../../auth/auth.service';
import { Match } from '../../_shared/models/match.model';
import { map, switchMap, takeLast, tap } from 'rxjs/operators';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { GameFormat } from '../../_shared/models/game.model';
import { ScoreFormat } from '../../_shared/models/score-format.model';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { MatchUpStatusEnum } from '../../_shared/models/csTypes/enums/matchup_status';

enum ErrorType {
  SET_TIEBREAK = 'SET_TIEBREAK',
  MATCH_TIEBREAK = 'MATCH_TIEBREAK',
  NOT_COMPLETED = 'NOT_COMPLETED',
}
interface IncorrectMatchUpScores {
  matchUp: Match;
  matchIndex: number;
  scoreIssues?: ScoreProblem[];
}

interface ScoreProblem {
  errorType: ErrorType;
  setIndex: number;
  numberOfPointsInTiebreak?: number;
}

@Injectable({ providedIn: 'root' })
export class TieDetailsService {
  private authService = inject(AuthService);
  private httpClient = inject(HttpClient);
  private bhs = inject(BoardHelpService);
  private updateTieAfterToggleAllowFansToScore$ = new Subject<boolean>();

  tie: Tie;

  constructor() {}

  async getTieInfo(
    tieId: any,
    skipCache: boolean,
    force = false,
    error: string
  ) {
    let errorLocation: string;

    if (!(typeof tieId === 'string')) {
      tieId = tieId._id;
      errorLocation = error;
    }

    if (force || !this.tie || this.tie?._id !== tieId) {
      this.tie = await lastValueFrom(
        this.httpClient
          .post<any>('api_endpoint' + `/tie/${tieId}/info`, {
            skipCache,
            errorLocation,
          })
          .pipe
            (map((res) => res.data),
            tap(({_id, score}) => {
              if (_id && score) {
                const [side1Score, side2Score] = score.split("-").map(Number);
                this.bhs.updateTieMatchScore(_id, { side1Score, side2Score });
              }
            }))
      );
      this.bhs.tie = this.tie;
      this.bhs.allowFansToScore = this.tie?.allowFansToScore || false;
      this.bhs.isUserFan = this.isUserFan(this.tie);
      this.bhs.totalFansCount = this.getUniqueFollowerCount(this.tie);
    }
    return this.tie;
  }

  getMatches(tieId, skipCache: boolean): Observable<Match[]> {
    return this.httpClient
      .post<any>('api_endpoint' + `/match/${tieId}/tie-matches`, { skipCache })
      .pipe(
        map((res) => res.data),
        tap((matchUps) => {
          this.bhs.matchUps = matchUps;
          const side1Score = this.getSideScore(matchUps, 1);
          const side2Score = this.getSideScore(matchUps, 2);
          this.bhs.updateTieMatchScore(this.tie._id, {
            side1Score,
            side2Score,
          });
        })
      );
  }

  addTracker(tieId: string, trackerIds: string[] = [], isInvited?: boolean, phone?: string, email?: string) {
    return this.httpClient.put<any>('api_endpoint' + `/tie/add-tracker`, {
      tieId,
      trackerIds,
      isInvited,
      phone,
      email,
    });
  }

  removeTracker(tieId: string, trackerId: string) {
    return this.httpClient.put<any>('api_endpoint' + `/tie/remove-tracker`, {
      tieId,
      trackerId,
    });
  }

  addOwner(tieId: string) {
    return this.httpClient.put<any>(
      'api_endpoint' + `/tie/${tieId}/add-owner`,
      {}
    );
  }

  removeOwner(tieId: string) {
    return this.httpClient.put<any>(
      'api_endpoint' + `/tie/${tieId}/remove-owner`,
      {}
    );
  }

  submitLineup(partner, tieId) {
    return this.httpClient.post<any>(
      'api_endpoint' + `/dual-match/submit-lineup`,
      { partner, tieId }
    );
  }

  submitScore(partner, tieId, matchUpIds) {
    return this.httpClient.post<any>(
      'api_endpoint' + `/dual-match/submit-score`,
      { partner, tieId, matchUpIds }
    );
  }

  async isOwner(tie: Tie): Promise<boolean> {
    let isOwner = false;

    if (
      tie.owners?.some(
        (owner) => owner === this.authService.authUserSignal()?._id
      )
    ) {
      isOwner = true;
    } else {
      tie?.sides?.forEach((side) => {
        if (
          side.team?.owners.some(
            (owner) => owner === this.authService.authUserSignal()?._id
          )
        ) {
          isOwner = true;
        }
      });
    }

    return isOwner;
  }

  async isOfficial(tie: Tie): Promise<boolean> {
    return tie.officials?.some(
      (official) => official.user === this.authService.authUserSignal()?._id
    );
  }

  async isTracker(tie: Tie): Promise<boolean> {
    return tie.trackers?.some(
      (tracker) =>
        tracker.tracker?._id === this.authService.authUserSignal()?._id
    );
  }

  async isTeamOwner(tie: Tie, sideNumber: number): Promise<boolean> {
    const side = tie?.sides?.[sideNumber];
    if (!side?.team?.owners?.length) {
      return false;
    }

    return side.team.owners.some(
      (owner) => owner === this.authService.authUserSignal()?._id
    );
  }

  // **** START Update tie status and score ****
  tieStatusUpdate(isSendNotification: boolean = false) {
    this.getMatches(this.tie._id, true)
      .pipe(
        takeLast(1),
        switchMap((matchUps) => {
          const side1Score = this.getSideScore(matchUps, 1);
          const side2Score = this.getSideScore(matchUps, 2);

          const { matchStatus, winningSide } = this.getTieStatus(
            this.tie,
            matchUps,
            side1Score,
            side2Score
          );
          const score = `${side1Score} - ${side2Score}`;
          this.bhs.updateTieMatchScore(this.tie._id, {
            side1Score,
            side2Score,
          });

          return this.httpClient.put<any>(
            'api_endpoint' + '/tie/update-status',
            { tieId: this.tie._id, winningSide, matchStatus, score, isSendNotification }
          );
        })
      )
      .subscribe();
  }

  private getTieStatus(
    tie: Tie,
    matchUps: Match[],
    side1Score: number,
    side2Score: number
  ): { matchStatus: string; winningSide: number | undefined } {
    if (matchUps.every((m) => m.matchStatus === 'TBP')) {
      return { matchStatus: 'TBP', winningSide: undefined };
    }

    if (this.tie.tieFormat.winCriteria.aggregateValue) {
      if (
        !matchUps.some(
          (m) =>
            m.matchStatus === 'TBP' ||
            m.matchStatus === MatchUpStatusEnum.IN_PROGRESS ||
            m.matchStatus === MatchUpStatusEnum.SUSPENDED
        )
      ) {
        if (side1Score > side2Score)
          return { matchStatus: MatchUpStatusEnum.COMPLETED, winningSide: 1 };
        if (side2Score > side1Score)
          return { matchStatus: MatchUpStatusEnum.COMPLETED, winningSide: 2 };
      }
    } else if (
      side1Score !== side2Score &&
      side1Score >= tie.tieFormat.winCriteria.valueGoal
    ) {
      // Check if it is not tie and then find a potential winner. If the winner has enough points change status
      // if no winner or if it is tied keep it in progress
      return { matchStatus: MatchUpStatusEnum.COMPLETED, winningSide: 1 };
    } else if (
      side1Score !== side2Score &&
      side2Score >= tie.tieFormat.winCriteria.valueGoal
    ) {
      return { matchStatus: MatchUpStatusEnum.COMPLETED, winningSide: 2 };
    }

    return {
      matchStatus: MatchUpStatusEnum.IN_PROGRESS,
      winningSide: undefined,
    };
  }

  public getSideScore(matches: Match[], sideNumber) {
    let sideScore = 0;
    this.tie?.tieFormat?.collectionDefinitions.forEach((cd) => {
      const collectionMatches = matches.filter((match) => {
        if (match.collectionId === cd.collectionId) {
          return match;
        }
      });

      // If a collection has value then make sure that a team has won more points than assigned by collectionValue (D1 doubles)
      if (cd.collectionValue) {
        let collectionSideScore = 0;
        collectionMatches.forEach((match) => {
          if (match.winningSide === sideNumber) {
            collectionSideScore++;
          }
        });

        if (
          collectionSideScore >=
          Math.floor(collectionMatches.length / 2) + 1
        ) {
          sideScore++;
        }
      } else {
        // If each match in collection is worth X number of points add that number of points to a team
        collectionMatches.forEach((match: Match, j) => {
          if (this.tie?.tieFormat?.winCriteria.aggregateValue) {
            match.score?.sets?.forEach((set) => {
              const value =
                cd.collectionValueProfile[j].gameValue ||
                cd.collectionValueProfile[j].pointValue;
              sideScore += value * set[`side${sideNumber}Score`];
            });
          } else if (match.winningSide === sideNumber) {
            sideScore += cd.collectionValueProfile[j].matchUpValue;
          }
        });
      }
    });

    return sideScore;
  }

  // **** END Update tie status and score ****

  validateMatchScores(matchUps: Match[]): IncorrectMatchUpScores[] {
    const incorrectMatchUpScores = [];

    matchUps.forEach((m, i) => {
      incorrectMatchUpScores.push({
        matchUp: m,
        matchIndex: i,
        scoreIssues: [],
      });

      if (
        m.matchStatus !== MatchUpStatusEnum.COMPLETED &&
        m.matchStatus !== MatchUpStatusEnum.RETIRED &&
        m.matchStatus !== MatchUpStatusEnum.INCOMPLETE &&
        m.matchStatus !== MatchUpStatusEnum.WALKOVER &&
        m.matchStatus !== MatchUpStatusEnum.DEFAULTED &&
        m.matchStatus !== MatchUpStatusEnum.ABANDONED
      ) {
        incorrectMatchUpScores[i].scoreIssues.push({
          errorType: ErrorType.NOT_COMPLETED,
        });
      } else if (m.score) {
        const scoreFormat: ScoreFormat = this.bhs.getScoreFormat(
          m.matchFormat,
          m.discipline
        );
        m.sets.forEach((s, j) => {
          const lastGame = s.games[s.games.length - 1];

          // In case last game is tiebreak check for errors scoring tiebreak
          if (lastGame.gameFormat === GameFormat.TIEBREAK) {
            // Check if the last set (match) tiebreak is not played
            // Check if side1TiebreakScore is undefined or
            // If use started to track tiebreak but they never completed
            if (
              scoreFormat.lastSetTiebreakTo > 0 &&
              j + 1 === scoreFormat.numberOfSets
            ) {
              if (
                !m.score.sets[j].side1TiebreakScore ||
                !m.score.sets[j].side2TiebreakScore ||
                (m.score.sets[j].side1TiebreakScore <
                  scoreFormat.lastSetTiebreakTo &&
                  m.score.sets[j].side2TiebreakScore <
                    scoreFormat.lastSetTiebreakTo)
              ) {
                incorrectMatchUpScores[i].scoreIssues.push({
                  errorType: ErrorType.MATCH_TIEBREAK,
                  setIndex: j,
                  numberOfPointsInTiebreak: scoreFormat.lastSetTiebreakTo,
                });
              }
            } else if (
              // Is this needed?
              m.matchStatus === MatchUpStatusEnum.COMPLETED &&
              (m.score.sets[j].side1TiebreakScore == null ||
                m.score.sets[j].side2TiebreakScore == null ||
                (m.score.sets[j].side1TiebreakScore < scoreFormat.tiebreakTo &&
                  m.score.sets[j].side2TiebreakScore < scoreFormat.tiebreakTo))
            ) {
              incorrectMatchUpScores[i].scoreIssues.push({
                errorType: ErrorType.SET_TIEBREAK,
                setIndex: j,
                numberOfPointsInTiebreak: scoreFormat.tiebreakTo,
              });
            }
          }
        });
      } else {
        //console.log('Invalid score object');
      }
    });

    return incorrectMatchUpScores;
  }

  public getScore(score: string = ''): string[] {
    /**
     * Retrieves the score of the match.
     * - If `score` is a string, it splits it using '-' as a delimiter.
     * - If `score` is not a string (i.e., initially an object before updates arrive via socket),
     *   it falls back to `this.tie.score` from the info API response.
     *
     * @param score - The current score, either as a string or an object (before socket updates).
     * @returns An array containing the split score values.
     */
    return typeof score === 'string'
      ? score.split('-')
      : this.tie.score.split('-');
  }

  public recalculateTieScore(match: Match):void {

  }

  isUserFan(tie: any): boolean {
    let isFan = false;
    

    tie.sides.forEach((side) => {
      if (side.team?.viewers?.some((viewer) => viewer === this.authService.authUserSignal()?._id)) {
        isFan = true;
      }
    });    
    return isFan;
  }

  getUniqueFollowerCount(tie: any): number {
    const all = (tie?.sides ?? [])
      .flatMap((side: any) => side?.team?.viewers ?? [])
      .map((v: any) => (typeof v === 'string' ? v : (v?._id ?? v).toString()));
    return new Set(all).size;
  }

  updateTieAfterToggleAllowFansToScore(allowFansToScore: boolean) {
    this.updateTieAfterToggleAllowFansToScore$.next(allowFansToScore);
  }

  getTieAfterToggleAllowFansToScore() {
    return this.updateTieAfterToggleAllowFansToScore$.asObservable();
  }

}
